import { PRODUCTS, VARIABLE_LABELS, SUBSCRIPTION_LABELS, CAMPAIGN_LABELS } from './feesConfig'
import { formatVariable, formatEur, formatPct, listOf } from './format'

// Grille des frais en vigueur, telle que le fees-manager l'applique (valeurs lues dans son environnement et Redis)
const FeesSchedule = ({ schedule }) => {
  const byProduct = (key) => schedule.variables.filter(v => v.product === key)
  const value = (name) => schedule.variables.find(v => v.name === name)?.value

  return (
    <div className='fees_grid'>
      <Promotions promotions={schedule.promotions} subscriptions={schedule.subscriptions} />

      {PRODUCTS.map(product => (
        <section key={product.key} className='fees_card'>
          <h2>{product.title}</h2>
          <p className='fees_note'>{product.note}</p>
          {product.key === 'margin' && <MarginTiers value={value} />}
          <table className='fees_table'>
            <tbody>
              {byProduct(product.key)
                .filter(v => product.key !== 'margin' || !/^MARGIN_(EU|US)_/.test(v.name))
                .map(v => (
                  <tr key={v.name}>
                    <td>{VARIABLE_LABELS[v.name] || v.name}<span className='fees_var_name'>{v.name}</span></td>
                    <td className={`fees_value ${v.value === null ? 'fees_value_missing' : ''}`}>{formatVariable(v)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          {product.key === 'broker' && <BrokerFees rows={schedule.broker_fees} />}
        </section>
      ))}
    </div>
  )
}

// Paliers du levier Exante : marchés en % (EU) et marchés par action (US, TMX)
const MarginTiers = ({ value }) => {
  const label = (bounds, i) => i < bounds.length ? `≤ ${formatEur(bounds[i], 0)}` : `> ${formatEur(bounds[bounds.length - 1], 0)}`
  const eu = listOf(value('MARGIN_EU_TIERS_MAX')), euPerc = listOf(value('MARGIN_EU_PERC'))
  const us = listOf(value('MARGIN_US_TIERS_MAX')), usPerc = listOf(value('MARGIN_US_PERC'))
  const usMin = listOf(value('MARGIN_US_MIN_FEE')), usShare = listOf(value('MARGIN_US_PER_SHARE'))
  return (
    <div className='fees_tiers'>
      <h3>Actions EU (marchés en %)</h3>
      <table className='fees_table fees_table_head'>
        <thead><tr><th>Position</th><th>Commission</th><th>Minimum</th></tr></thead>
        <tbody>
          {euPerc.map((p, i) => (
            <tr key={i}><td>{label(eu, i)}</td><td>{formatPct(p)}</td><td>{formatEur(value('MARGIN_EU_MIN_FEE'))}</td></tr>
          ))}
        </tbody>
      </table>
      <h3>Actions US / TMX (marchés par action)</h3>
      <table className='fees_table fees_table_head'>
        <thead><tr><th>Position</th><th>Commission</th><th>Par action</th><th>Minimum</th></tr></thead>
        <tbody>
          {usPerc.map((p, i) => (
            <tr key={i}><td>{label(us, i)}</td><td>{formatPct(p)}</td><td>{formatEur(usShare[i], 3)}</td><td>{formatEur(usMin[i])}</td></tr>
          ))}
        </tbody>
      </table>
      <p className='fees_note'>Commission = la plus haute des trois (minimum, % de la position, par action). Copy : même grille avec le minimum copy.</p>
    </div>
  )
}

// Coût réel des brokers (Redis broker_fees:<broker>:<EXCHANGE>)
const BrokerFees = ({ rows }) => (
  <div className='fees_tiers'>
    <h3>Coût broker par marché</h3>
    <table className='fees_table fees_table_head'>
      <thead><tr><th>Broker</th><th>Marché</th><th>Coût</th><th>Minimum</th><th>Plancher</th></tr></thead>
      <tbody>
        {rows.map(r => (
          <tr key={`${r.broker}:${r.exchange}`}>
            <td>{r.broker}</td>
            <td>{r.exchange}</td>
            <td>{r.kind === 'percent' ? formatPct(r.rate_pct) : r.kind === 'fixed_per_share' ? `${r.per_share} ${r.min_currency} / action` : 'aucun'}</td>
            <td>{r.min ? `${r.min} ${r.min_currency}` : '—'}</td>
            <td>{r.floor ? 'Oui' : 'Non'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

// Remises : abonnements et promotions en cours
const Promotions = ({ promotions, subscriptions }) => (
  <section className='fees_card fees_card_wide'>
    <h2>Remises en vigueur</h2>
    <p className='fees_note'>Appliquées après la grille, puis plancher broker. Les campagnes ne s’appliquent jamais au copy.</p>
    <div className='fees_promos'>
      <div>
        <h3>Abonnements</h3>
        <table className='fees_table'>
          <tbody>
            {subscriptions.map(s => (
              <tr key={s.subscription_type}><td>{SUBSCRIPTION_LABELS[s.subscription_type] || s.subscription_type}</td><td className='fees_value'>{formatPct(s.discount, 2)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <h3>Promotions en cours</h3>
        {!promotions.reduced_fees && promotions.campaigns.length === 0 && <p className='fees_note'>Aucune.</p>}
        <table className='fees_table'>
          <tbody>
            {promotions.reduced_fees && (
              <tr>
                <td>Réduction globale ({[promotions.reduced_fees.platform_fees && 'classique', promotions.reduced_fees.margin_fees && 'levier'].filter(Boolean).join(' + ') || 'aucun produit'})
                  {promotions.reduced_fees.quantity ? `, ${promotions.reduced_fees.quantity} trades` : ''}
                  {promotions.reduced_fees.end_date ? `, jusqu’au ${new Date(promotions.reduced_fees.end_date).toLocaleDateString('fr-FR')}` : ''}</td>
                <td className='fees_value'>{formatPct(promotions.reduced_fees.percentage, 2)}</td>
              </tr>
            )}
            {promotions.campaigns.map(c => (
              <tr key={c.id}>
                <td>{CAMPAIGN_LABELS[c.type] || c.type}{c.category ? ` (${c.category})` : ''}
                  {c.end_date ? `, jusqu’au ${new Date(c.end_date).toLocaleDateString('fr-FR')}` : ''}</td>
                <td className='fees_value'>{c.percentage !== null ? formatPct(c.percentage, 2) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
)

export default FeesSchedule
